import { all, call, debounce, fork, put, select, takeLatest, delay } from 'redux-saga/effects';
import i18n from 'i18next';

import {
  ADD_LOCATIONS_TO_PARENT, CREATE_LOCATION, EDIT_LOCATION, FETCH_LOCATIONS, FETCH_LOCATIONS_FACETS,
  FETCH_USER_LOCATIONS, FETCH_USER_LOCATIONS_ROLES, REMOVE_LOCATIONS, SET_LOCATION_SYSTEM_MODE_BULK,
  CREATE_BULK_UNITS,
} from '../constants/ActionTypes';

import { getUserById } from '../api/User';
import { selectSingleSelectOrSelectedItems, selectSingleSelectLocation } from 'selectors';
import { disableApiCache, showError, showMessage } from '../actions/Common';
import { CustomAction, ListViewRow } from 'model/state';
import {
  addToParentError, addToParentSuccess, createLocationError, createLocationSuccess, fetchLocationFacetsError,
  fetchLocationFacetsSuccess, fetchLocationsError, fetchLocationsSuccess, fetchUserLocationsError,
  fetchUserLocationsRolesError, fetchUserLocationsRolesSuccess, fetchUserLocationsSuccess, fetchUserSuccess,
  LocationPayload, removeLocationsError, removeLocationsSuccess, removeSingleSelectLocation, setLocationSystemModeBulkSuccess,
  createBulkUnitsRequest, createBulkUnitsSuccess, createBulkUnitsError,
} from 'actions';
import {
  buildLocationQuery, buildSortQuery, fromLocation, fromLocationType, fromSystemMode, GetLocationsFacetResponse, GetLocationsResponse,
  LocationResponse, LocationSystemModePayload, removeNonAuthorizedParent, toFacetParameters, toLocation,
  toLocationFacetData, toLocationsWithRoles, toUser, UserResponse
} from 'model/api';
import {
  createLocation, editLocation, getFacets, getLocationById, getLocations, removeLocation, setLocationSystemMode
} from 'api/Location';
import { Location, LocationType, MessageType, SystemMode, typeToString, User } from 'model';

const WAIT_FOR_MIGRATION_DELAY_MS = 2000;

function* fetchLocationsWorker(action: CustomAction<LocationPayload>) {
  const userId = action.payload?.userId;
  const page = action.payload?.page;
  const search = action.payload?.search;
  const filters = action.payload?.filters;
  const sortQ = action.payload?.sort ? buildSortQuery(action.payload?.sort) : '';
  try {
    if (!userId || page === undefined || search === undefined || filters === undefined) {
      throw Error('Invalid Parameters');
    }

    const searchQ = search === '' ? undefined : search;

    let locationList;
    let parent: Location | null;
    let resultPage = 1;
    let total = 0;
    if (action.payload?.parentId) {
      const [parentData, childrenResponse]: [LocationResponse, GetLocationsResponse] = yield all([
        call(getLocationById, action.payload.parentId, ['parent']),
        call(getLocations,  buildLocationQuery(
          userId, page, filters, searchQ, sortQ, action.payload.parentId, false, undefined, action.payload.cached
        )),
      ]);

      locationList = toLocationsWithRoles(childrenResponse.items || [], userId);
      total = childrenResponse.total;
      resultPage = childrenResponse.page;
      parent = removeNonAuthorizedParent(parentData, userId);
    } else {
      // root view (all locations)
      const locationResponse: GetLocationsResponse = yield call(getLocations, buildLocationQuery(
        userId, page, filters, searchQ, sortQ, undefined, true, undefined, action.payload?.cached
      ));

      locationList = toLocationsWithRoles(locationResponse.items, userId);
      resultPage = locationResponse.page;
      total = locationResponse.total;
      parent = null;
    }

    yield put(fetchLocationsSuccess(locationList, parent, resultPage, total));
  } catch (err) {
    yield put(fetchLocationsError());
    yield put(showError(i18n.t('connection_error'), err));
  }
}

function* fetchUserLocationsWorker(action: CustomAction<LocationPayload>) {
  const userId = action.payload?.userId;
  const types = action.payload?.types;
  const page = action.payload?.page;
  const search = action.payload?.search;
  try {
    if (userId === undefined || types === undefined || page === undefined) {
      throw Error('Invalid Parameters');
    }

    const searchQ = search === '' ? undefined : search;
    const locationResponse: GetLocationsResponse = yield call(getLocations, {
      userId,
      class: types.length === 0 ? undefined : types.map(t => fromLocationType(t).key).join(','),
      q: searchQ,
      page,
      size: 15,
    });

    const locations = action.payload?.withRoles ?
      toLocationsWithRoles(locationResponse.items, userId) :
      locationResponse.items.map((location: LocationResponse) => toLocation(location));

    yield put(fetchUserLocationsSuccess(locations, locationResponse.page, locationResponse.total));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(fetchUserLocationsError());
  }
}

function* fetchUserLocationsRolesWorker(action: CustomAction<LocationPayload>) {
  const authUserId = action.payload?.authUserId;
  const currentUserId = action.payload?.currentUserId;
  const page = action.payload?.page;
  const search = action.payload?.search;
  const filters = action.payload?.filters;
  try {
    if (!authUserId || !currentUserId || page === undefined || search === undefined || filters === undefined) {
      throw Error('Invalid Parameters');
    }

    const searchQ = search === '' ? undefined : search;
    let parent: Location | null;
    let currentUserData: User;
    let locations: Location[];
    let resultPage = 1;
    let total = 0;
    if (action.payload?.parentId) {
      const [parentLocation, currentUser, childrenResp]: [LocationResponse, UserResponse, GetLocationsResponse] = yield all([
        call(getLocationById, action.payload.parentId, ['parent']),
        call(getUserById, currentUserId),
        call(getLocations,  buildLocationQuery(authUserId, page, {}, searchQ, '', action.payload.parentId, false)),
      ]);
      locations = toLocationsWithRoles(childrenResp.items, currentUserId);
      parent = removeNonAuthorizedParent(parentLocation, authUserId);

      total = childrenResp.total;
      resultPage = childrenResp.page;
      currentUserData = toUser(currentUser);
    } else {
      const [locationResponse, currentUser]: [GetLocationsResponse, UserResponse] = yield all([
        call(getLocations, buildLocationQuery(authUserId, page, {}, searchQ)),
        call(getUserById, currentUserId)
      ]);
      locations = toLocationsWithRoles(locationResponse.items, currentUserId);
      resultPage = locationResponse.page;
      total = locationResponse.total;
      parent = null;

      currentUserData = toUser(currentUser);
    }

    yield put(fetchUserLocationsRolesSuccess(locations, parent, resultPage, total));
    yield put(fetchUserSuccess(currentUserData));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(fetchUserLocationsRolesError());
  }
}

function* createLocationsWorker(action: CustomAction<LocationPayload>) {
  const location = action.payload?.partialLocation;
  try {
    if (!location) {
      throw Error('Invalid Parameters');
    }
    const response = yield call(createLocation, fromLocation(location));

    // give enough time to migration lambda to persist location updates in pg
    yield delay(WAIT_FOR_MIGRATION_DELAY_MS);
    // this will let the fetch request know that it should disable api caching
    yield put(disableApiCache());
    yield put(showMessage({
      message: i18n.t('create-location-success-title', { locationType: typeToString(location.type || LocationType.UNIT) }),
      description: i18n.t('create-location-success-desc', { locationType: typeToString(location.type || LocationType.UNIT), addressLink: `<a href="/${location.type === LocationType.UNIT ? 'unit' : 'locations'}/${response.id}">${location.nickname || location.address}</a>` }),
      type: MessageType.SUCCESS
    }));
    yield put(createLocationSuccess());
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(createLocationError());
  }
}

function* createBulkUnitsWorker(action: CustomAction<LocationPayload>) {
  const unitNames = action.payload?.unitNames;
  const parent = action.payload?.parent;
  try {
    if (!unitNames || !parent) {
      throw Error('Invalid Parameters');
    }
    yield put(createBulkUnitsRequest());

    for (let i = 0; i < unitNames.length; ++i) {
      const unitName = unitNames[i];
      const partialLocation: Partial<Location> = {
        nickname: unitName,
        type: LocationType.UNIT,
        address: parent.address,
        address2: parent.address2,
        city: parent.city,
        state: parent.state,
        country: parent.country,
        postalCode: parent.postalCode,
        timezone: parent.timezone,
        account: parent.account,
        parent,
      };
      yield put(showMessage({
        message: i18n.t('add-bulk-units-progress-title', { unitNumber: i+1, totalUnits: unitNames.length }),
        description: i18n.t('add-bulk-units-progress-desc', { unitName }),
        type: MessageType.INFO,
      }));
      yield call(createLocation, fromLocation(partialLocation));
    }

    // give enough time to migration lambda to persist location updates in pg
    yield delay(WAIT_FOR_MIGRATION_DELAY_MS);
    // this will let the fetch request know that it should disable api caching
    yield put(disableApiCache());
    yield put(showMessage({
      message: i18n.t('add-bulk-units-success-title'),
      description: i18n.t('add-bulk-units-success-desc', { numUnits: unitNames.length }),
      type: MessageType.SUCCESS
    }));
    yield put(createBulkUnitsSuccess());
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(createBulkUnitsError());
  }
}

function* editLocationsWorker(action: CustomAction<LocationPayload>) {
  const location = action.payload?.partialLocation;
  try {
    if (location === undefined || location.id === undefined) {
      throw Error('Invalid Parameters');
    }
    const response = yield call(editLocation, location.id, fromLocation(location));

    // give enough time to migration lambda to persist location updates in pg
    yield delay(WAIT_FOR_MIGRATION_DELAY_MS);
    // this will let the fetch request know that it should disable api caching
    yield put(disableApiCache());
    yield put(showMessage({
      message: i18n.t('edit-location-success-title', { locationType: typeToString(location.type || LocationType.UNIT) }),
      description: i18n.t('edit-location-success-desc', { locationType: typeToString(location.type || LocationType.UNIT), addressLink: `<a href="/${location.type === LocationType.UNIT ? 'unit' : 'locations'}/${response.id}">${location.nickname || location.address}</a>` }),
      type: MessageType.SUCCESS
    }));
    yield put(createLocationSuccess());
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(createLocationError());
  }
}

function validateSelection(selected: Array<ListViewRow<Location>>, parentType: LocationType): { message: string, description: string } | null {
  if (parentType === LocationType.PROPERTY) {
    const invalidSelection = selected.find(loc => loc.type !== LocationType.UNIT);
    if (invalidSelection !== undefined) {
      return {
        message: i18n.t('add-to-parent-invalid-selection-title'),
        description: `${i18n.t('add-to-parent-invalid-selection-desc-1')} ${invalidSelection.type === LocationType.REGION ? i18n.t('Community') : invalidSelection.type === LocationType.PROPERTY ? i18n.t('Property') : i18n.t('Building')} (${invalidSelection.address}) ${i18n.t('add-to-parent-invalid-selection-desc-2')} ${i18n.t('Property')}`,
      };
    }
  } else if (parentType === LocationType.BUILDING) {
    const invalidSelection = selected.find(loc => loc.type !== LocationType.UNIT);
    if (invalidSelection !== undefined) {
      return {
        message: i18n.t('add-to-parent-invalid-selection-title'),
        description: `${i18n.t('add-to-parent-invalid-selection-desc-1')} ${invalidSelection.type === LocationType.REGION ? i18n.t('Community') : invalidSelection.type === LocationType.PROPERTY ? i18n.t('Property') : i18n.t('Building')} (${invalidSelection.address}) ${i18n.t('add-to-parent-invalid-selection-desc-2')} ${i18n.t('Building')}`,
      };
    }
  } else if (parentType === LocationType.REGION) {
    const invalidSelection = selected.find(loc => loc.type === LocationType.REGION);
    if (invalidSelection !== undefined) {
      return {
        message: i18n.t('add-to-parent-invalid-selection-title'),
        description: `${i18n.t('add-to-parent-invalid-selection-desc-1')} ${i18n.t('Community')} (${invalidSelection.address}) ${i18n.t('add-to-parent-invalid-selection-desc-2')} ${i18n.t('Community')}`,
      };
    }
  }
  return null;
}

function* addLocationToParentWorker(action: CustomAction<LocationPayload>) {
  const parentId = action.payload?.parentId;
  const parentType = action.payload?.parentType;
  const selected: Array<ListViewRow<Location>> = yield select(selectSingleSelectOrSelectedItems());
  try {
    if (!parentId || !parentType) {
      throw Error('Invalid Parameters');
    }

    const validationResult = validateSelection(selected, parentType);
    if (validationResult !== null) {
      yield put(showMessage({ message: validationResult.message, description: validationResult.description, type: MessageType.ERROR }));
      yield put(addToParentError());
    } else {
      const apiCalls = [];
      for (let index = 0; index < selected.length; index++) {
        apiCalls.push(call(editLocation, selected[index].id as string, {
          parent: { id: parentId }
        }));
      }
      yield all(apiCalls);
      yield put(disableApiCache());
      yield put(showMessage({ message: i18n.t('add-to-parent-success-title'), description: i18n.t('add-to-parent-success-desc'), type: MessageType.SUCCESS }));
      yield put(addToParentSuccess(selected.map(loc => loc.id as string)));
    }
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(addToParentError());
  } finally {
    const singleSelectLocation = yield select(selectSingleSelectLocation());
    if (singleSelectLocation !== null && selected.length > 0) {
      yield put(removeSingleSelectLocation(selected[0]));
    }
  }
}

function* removeLocationWorker(action: CustomAction<LocationPayload>) {
  const selected: Array<ListViewRow<Location>> = yield select(selectSingleSelectOrSelectedItems());
  try {
    if (selected.length < 1) {
      yield put(showMessage({
        message: i18n.t('remove-no-selection-title'),
        description: i18n.t('remove-no-selection-desc'),
        type: MessageType.ERROR
      }));
      yield put(removeLocationsError());
    } else {
      const apiCalls = [];
      for (let index = 0; index < selected.length; index++) {
        apiCalls.push(call(removeLocation, selected[index].id as string));
      }
      yield all(apiCalls);

      // give enough time to migration lambda to persist location updates in pg
      yield delay(WAIT_FOR_MIGRATION_DELAY_MS);
      // this will let the fetch request know that it should disable api caching
      yield put(disableApiCache());
      yield put(showMessage({ message: i18n.t('remove-success-title'), description: i18n.t('remove-success-desc'), type: MessageType.SUCCESS }));
      yield put(removeLocationsSuccess());
    }
  } catch (err) {
    if (err.response && err.response.status === 409) {
      yield put(showMessage({ message: i18n.t('remove-has-children-error-title'), description: i18n.t(err.response.data.message), type: MessageType.ERROR }));
    } else {
      yield put(showError(i18n.t('connection_error'), err));
    }
    yield put(removeLocationsError());
  } finally {
    const singleSelectLocation = yield select(selectSingleSelectLocation());
    if (singleSelectLocation !== null && selected.length > 0) {
      yield put(removeSingleSelectLocation(selected[0]));
    }
  }
}

function* setLocationSystemModeBulkWorker(action: CustomAction<LocationPayload>) {
  const revertMinutes = action.payload?.revertMinutes;
  const target = action.payload?.systemModeTarget;
  const selected: Array<ListViewRow<Location>> = yield select(selectSingleSelectOrSelectedItems());
  try {
    if (target === undefined || (revertMinutes === undefined && target === SystemMode.SLEEP)) {
      throw Error('Invalid Parameters');
    }

    if (selected.length < 1) {
      yield put(showMessage({
        message: i18n.t('remove-no-selection-title'),
        description: i18n.t('remove-no-selection-desc'),
        type: MessageType.ERROR
      }));
    } else {
      const apiCalls = [];
      for (let index = 0; index < selected.length; index++) {
        let systemModePayload: LocationSystemModePayload;
        if (target === SystemMode.SLEEP) {
          const currentMode = selected[index].systemMode?.target || SystemMode.HOME;
          systemModePayload = {
            revertMinutes,
            target: fromSystemMode(SystemMode.SLEEP),
            revertMode: fromSystemMode(currentMode === SystemMode.SLEEP ? SystemMode.HOME : currentMode),
            shouldCascade: true,
          };
        } else {
          systemModePayload = {
            target: fromSystemMode(target),
            shouldCascade: true,
          };
        }
        apiCalls.push(call(setLocationSystemMode, selected[index].id as string, systemModePayload));
      }
      yield all(apiCalls);

      const locationIds = selected.map(loc => loc.id as string);
      yield put(setLocationSystemModeBulkSuccess(locationIds, target));
      yield put(disableApiCache());
      yield put(showMessage({ message: i18n.t('location-system-mode-success-title'), description: i18n.t('location-system-mode-success-desc'), type: MessageType.SUCCESS }));
    }
  } catch (err) {
    if (err.response && err.response.status === 409 && err.response.data?.message?.includes('locked')) {
      yield put(showMessage({ message: i18n.t('location-system-mode-fail-title'), description: i18n.t('location-system-mode-locked-desc'), type: MessageType.ERROR }));
    } else {
      yield put(showError(i18n.t('connection_error'), err));
    }
  } finally {
    const singleSelectLocation = yield select(selectSingleSelectLocation());
    if (singleSelectLocation !== null && selected.length > 0) {
      yield put(removeSingleSelectLocation(selected[0]));
    }
  }
}

function* fetchLocationFacetsWorker(action: CustomAction<LocationPayload>) {
  const facets = action.payload?.facet;
  try {
    if (!facets) {
      throw Error('Invalid Parameters');
    }

    const response: GetLocationsFacetResponse = yield call(getFacets, toFacetParameters(facets));
    yield put(fetchLocationFacetsSuccess(toLocationFacetData(response)));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(fetchLocationFacetsError());
  }
}

export function* fetchLocationsSaga() {
  yield debounce(500, FETCH_LOCATIONS, fetchLocationsWorker);
}

export function* createLocationsSaga() {
  yield takeLatest(CREATE_LOCATION, createLocationsWorker);
}

export function* createBulkUnitsSaga() {
  yield takeLatest(CREATE_BULK_UNITS, createBulkUnitsWorker);
}

export function* fetchUserLocationsSaga() {
  yield debounce(500, FETCH_USER_LOCATIONS, fetchUserLocationsWorker);
}

export function* addLocationToParentSaga() {
  yield takeLatest(ADD_LOCATIONS_TO_PARENT, addLocationToParentWorker);
}

export function* removeLocationSaga() {
  yield takeLatest(REMOVE_LOCATIONS, removeLocationWorker);
}

export function* fetchUserLocationsRolesSaga() {
  yield debounce(500, FETCH_USER_LOCATIONS_ROLES, fetchUserLocationsRolesWorker);
}

export function* editLocationsSaga() {
  yield takeLatest(EDIT_LOCATION, editLocationsWorker);
}

export function* setLocationSystemModeBulkSaga() {
  yield takeLatest(SET_LOCATION_SYSTEM_MODE_BULK, setLocationSystemModeBulkWorker);
}

export function* fetchLocationFacetsSaga() {
  yield takeLatest(FETCH_LOCATIONS_FACETS, fetchLocationFacetsWorker);
}

export default function* rootSaga() {
  yield all([
    fork(fetchLocationsSaga),
    fork(createLocationsSaga),
    fork(createBulkUnitsSaga),
    fork(fetchUserLocationsSaga),
    fork(addLocationToParentSaga),
    fork(removeLocationSaga),
    fork(fetchUserLocationsRolesSaga),
    fork(editLocationsSaga),
    fork(setLocationSystemModeBulkSaga),
    fork(fetchLocationFacetsSaga),
  ]);
}
